/**
 * 剪贴板历史 (per-serial) 的核心读写逻辑。
 *
 * 设计要点：
 *   - 数据落到用户级 prefs (`B2 / prefs.devices.json`) 的 `clipboard.entries`
 *     子节，复用现成的 `useDevicePrefs / useDevicePrefsUpdate` 通道；
 *     debounce + 原子写都已在 `web/src/lib/myPrefs.ts` 里做完。
 *   - 没有自带 zustand store —— 直接读 prefs 的 selector，避免 SPA 内
 *     一份本地缓存 + 一份服务器副本两两不同步。
 *   - `entries` 是数组，`prefs.devices.json` 的 deep-merge 对数组是
 *     "整体替换"语义，所以每次写入都拿当前 entries → 计算新数组 →
 *     回填整个 `clipboard` 子节。这是有意为之：append-merge 会让
 *     LRU 截断 / 去重 / 排序失效。
 *
 * 上层逻辑（去重 / LRU / pinned 排序）抽成下面的纯函数，方便单测；
 * `useClipboardHistory` / `useClipboardActions` 只是把它们粘到 React
 * 数据流上。
 */
import { useCallback } from 'react';
import {
  useDevicePrefs,
  useDevicePrefsUpdate,
  useMyPrefsStore,
  type ClipboardEntry,
  type ClipboardDevicePrefs,
} from '@/lib/myPrefs';
import { useMirror } from '@/store/mirror';

export type { ClipboardEntry, ClipboardDevicePrefs } from '@/lib/myPrefs';

/** Per-bucket cap. Pinned entries are NEVER counted toward this limit. */
export const CLIPBOARD_LRU_LIMIT = 100;

/* ----------------------------------------------------------------- */
/* Pure helpers (single source of truth, fully unit-tested)          */
/* ----------------------------------------------------------------- */

/**
 * Stable sort: pinned items first (newest-first within), then non-pinned
 * (newest-first). Equivalent to `[...].sort((a,b) => ...)` but written
 * out so the comparator's intent stays inspectable.
 */
export function sortEntries(entries: readonly ClipboardEntry[]): ClipboardEntry[] {
  return [...entries].sort((a, b) => {
    const ap = a.pinned ? 1 : 0;
    const bp = b.pinned ? 1 : 0;
    if (ap !== bp) return bp - ap;
    return b.createdAt - a.createdAt;
  });
}

/**
 * Apply LRU cap to non-pinned entries. Pinned entries always survive
 * (collecting too many pins is a feature — user explicitly chose them).
 *
 * Assumes input is already sorted via {@link sortEntries}.
 */
function applyLru(entries: ClipboardEntry[], limit: number): ClipboardEntry[] {
  if (entries.length <= limit) return entries;
  const pinned = entries.filter((e) => e.pinned);
  const nonPinned = entries.filter((e) => !e.pinned);
  const allowedNonPinned = Math.max(0, limit - pinned.length);
  const trimmedNonPinned = nonPinned.slice(0, allowedNonPinned);
  return sortEntries([...pinned, ...trimmedNonPinned]);
}

/**
 * Insert a new clipboard entry, with two pieces of intelligence:
 *
 *   1. **Dedup by (text, source) composite key.** If a row with the
 *      *same text AND same source* already exists, it's bumped to the
 *      top with a fresh `createdAt` — `source` is **never** overridden,
 *      so a host-side "abc" and a device-side "abc" coexist as two
 *      independent rows in their respective columns. This was a bug
 *      in the first cut: device pushes were silently rewriting host
 *      entries' `source` field, making the row "jump columns".
 *   2. **LRU.** Non-pinned tail is trimmed to {@link CLIPBOARD_LRU_LIMIT}.
 *
 * Empty / whitespace-only text is dropped silently — the host clipboard
 * poll occasionally returns these on Linux when nothing is selected.
 */
export function pushEntry(
  entries: readonly ClipboardEntry[],
  patch: { text: string; source: ClipboardEntry['source']; createdAt?: number; id?: string },
): ClipboardEntry[] {
  const trimmed = patch.text;
  if (typeof trimmed !== 'string' || trimmed.length === 0) return [...entries];
  const now = patch.createdAt ?? Date.now();
  const existing = entries.find(
    (e) => e.text === trimmed && e.source === patch.source,
  );
  let next: ClipboardEntry[];
  if (existing) {
    next = entries.map((e) =>
      e.id === existing.id ? { ...e, createdAt: now } : e,
    );
  } else {
    const id = patch.id ?? makeEntryId();
    next = [
      ...entries,
      { id, text: trimmed, source: patch.source, createdAt: now },
    ];
  }
  return applyLru(sortEntries(next), CLIPBOARD_LRU_LIMIT);
}

export function removeEntry(
  entries: readonly ClipboardEntry[],
  id: string,
): ClipboardEntry[] {
  return entries.filter((e) => e.id !== id);
}

export function togglePinEntry(
  entries: readonly ClipboardEntry[],
  id: string,
): ClipboardEntry[] {
  const next = entries.map((e) =>
    e.id === id ? { ...e, pinned: !e.pinned } : e,
  );
  // Re-sort so pinned-on rows leap to the top, pinned-off rows fall
  // back into their createdAt slot. LRU does NOT re-trim here — un-
  // pinning may push us above the cap, but trimming would silently
  // delete a row the user can still see; we let it stay until the next
  // `pushEntry` reapplies the cap.
  return sortEntries(next);
}

export function updateEntry(
  entries: readonly ClipboardEntry[],
  id: string,
  patch: Partial<Pick<ClipboardEntry, 'text' | 'pinned'>>,
): ClipboardEntry[] {
  const next = entries.map((e) => {
    if (e.id !== id) return e;
    // `text` edits also bump `createdAt` so the edited row floats to
    // the top — matches user expectation ("I just touched this, why
    // is it buried?").
    const nextText = typeof patch.text === 'string' ? patch.text : e.text;
    const nextPinned = typeof patch.pinned === 'boolean' ? patch.pinned : e.pinned;
    const bumpedCreatedAt =
      typeof patch.text === 'string' && patch.text !== e.text
        ? Date.now()
        : e.createdAt;
    return { ...e, text: nextText, pinned: nextPinned, createdAt: bumpedCreatedAt };
  });
  return sortEntries(next);
}

/**
 * Generate a per-entry id. Uses `Math.random` + `Date.now` because the
 * SPA already does this in other stores (log-watch, checkpoint) and
 * we don't want a new dependency for ulid/nanoid. Collisions are
 * statistically irrelevant at human typing rates.
 */
export function makeEntryId(): string {
  return `c-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`;
}

/* ----------------------------------------------------------------- */
/* React hooks (thin wrappers over the prefs store)                  */
/* ----------------------------------------------------------------- */

/**
 * Subscribe to a serial's clipboard history. Returns the entries array
 * directly (already sorted; the writer always re-sorts before persist).
 *
 * `serial` may be empty / falsy when the user hasn't selected a device
 * yet — the hook falls back to the `defaultDevice` shape (also empty
 * array) so callers don't need to thread `serial ? ... : []` everywhere.
 */
export function useClipboardHistory(serial: string | null | undefined): ClipboardEntry[] {
  return useDevicePrefs(serial ?? '', (d) => d.clipboard.entries);
}

export function useClipboardLastSeenAt(
  serial: string | null | undefined,
): number | null {
  return useDevicePrefs(serial ?? '', (d) => d.clipboard.lastSeenAt);
}

export interface ClipboardActions {
  push: (patch: { text: string; source: ClipboardEntry['source'] }) => void;
  remove: (id: string) => void;
  togglePin: (id: string) => void;
  update: (id: string, patch: Partial<Pick<ClipboardEntry, 'text' | 'pinned'>>) => void;
  clear: () => void;
  /** Stamp a host-clipboard "we've seen this text" timestamp without a push (used by the poll guard). */
  noteSeenAt: (ts: number) => void;
}

/**
 * Returns a stable bag of writers for the given serial. Each call will
 * read the latest `entries` from the prefs store before computing the
 * next array, so concurrent host-poll vs. device-arrival pushes don't
 * race the in-memory cache.
 *
 * Falsy serials produce a no-op set so callers can `useClipboardActions(serial)`
 * unconditionally without `if`-guards at the call site.
 */
export function useClipboardActions(serial: string | null | undefined): ClipboardActions {
  const updateDevice = useDevicePrefsUpdate(serial ?? '');
  // We deliberately read the *latest* entries inside each closure via
  // `useDevicePrefs` snapshot. We can't call hooks from non-top-level
  // contexts, so `useDevicePrefs` runs at hook-init time and re-fires
  // on every state change — `entries` is captured fresh in each render.
  const entries = useClipboardHistory(serial);
  const lastSeenAt = useClipboardLastSeenAt(serial);

  const noop = !serial;

  const push = useCallback<ClipboardActions['push']>(
    (patch) => {
      if (noop) return;
      const next = pushEntry(entries, patch);
      updateDevice({
        clipboard: { entries: next, lastSeenAt: Date.now() },
      });
    },
    [noop, entries, updateDevice],
  );

  const remove = useCallback<ClipboardActions['remove']>(
    (id) => {
      if (noop) return;
      const next = removeEntry(entries, id);
      updateDevice({ clipboard: { entries: next, lastSeenAt } });
    },
    [noop, entries, lastSeenAt, updateDevice],
  );

  const togglePin = useCallback<ClipboardActions['togglePin']>(
    (id) => {
      if (noop) return;
      const next = togglePinEntry(entries, id);
      updateDevice({ clipboard: { entries: next, lastSeenAt } });
    },
    [noop, entries, lastSeenAt, updateDevice],
  );

  const update = useCallback<ClipboardActions['update']>(
    (id, patch) => {
      if (noop) return;
      const next = updateEntry(entries, id, patch);
      updateDevice({ clipboard: { entries: next, lastSeenAt } });
    },
    [noop, entries, lastSeenAt, updateDevice],
  );

  const clear = useCallback<ClipboardActions['clear']>(() => {
    if (noop) return;
    updateDevice({ clipboard: { entries: [], lastSeenAt } });
  }, [noop, lastSeenAt, updateDevice]);

  const noteSeenAt = useCallback<ClipboardActions['noteSeenAt']>(
    (ts) => {
      if (noop) return;
      updateDevice({ clipboard: { entries, lastSeenAt: ts } });
    },
    [noop, entries, updateDevice],
  );

  return { push, remove, togglePin, update, clear, noteSeenAt };
}

/* ----------------------------------------------------------------- */
/* Imperative entry — used from non-component hot paths              */
/* ----------------------------------------------------------------- */

/**
 * Imperative push that does not require being inside a React render.
 *
 * Use case: `CopyButton.writeClipboard` is a vanilla async function
 * called from event handlers; it can't host hooks. We want every
 * SPA-internal Cmd+C of a chunk of text to land in the history if a
 * mirror is open, without threading a hook into every callsite.
 *
 * The function reads the current active mirror serial from `useMirror`
 * directly. **No active mirror → no-op**, matching the plan's
 * decision: "不开镜像时不捕获主机剪贴板"; this prevents the SPA from
 * silently writing PC clipboard contents into prefs storage when the
 * user is just browsing settings / dashboards.
 */
export function pushHostClipTextToActiveMirror(text: string): void {
  if (!text) return;
  const serial = useMirror.getState().serial;
  if (!serial) return;
  const prefs = useMyPrefsStore.getState();
  const cur = prefs.devices[serial]?.clipboard?.entries ?? [];
  // Cheap fast-fail against the latest existing host entry with the
  // same text — avoids waking the debounced PUT pipeline when the
  // user clicks a CopyButton repeatedly on the same string. We only
  // dedup within the host source so a previously-cached device "abc"
  // doesn't suppress a fresh host "abc" (different column entirely).
  // The downstream `pushEntry` enforces the same (text, source) key.
  if (cur.some((e) => e.source === 'host' && e.text === text)) return;
  const next = pushEntry(cur, { text, source: 'host' });
  prefs.updateDevice(serial, {
    clipboard: { entries: next, lastSeenAt: Date.now() },
  });
}
